import { zodResolver } from '@hookform/resolvers/zod'
import { Building2, Loader2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { FormSelect } from '@/components/ui/select'
import { isApiError } from '@/lib/api/errors'
import { useSaveParty } from '../api'
import { isValidTrIban } from '../iban'
import type { Counterparty } from '../types'
import { toastError } from '../ui'
import { CheckRow, Field, FormSection } from './FormBits'

export function PartyForm({ party, onDone }: { party: Counterparty | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveParty()
  const schema = z.object({
    name: z.string().trim().min(2, t('finance.parties.nameRequired')).max(120),
    kind: z.enum(['supplier', 'customer']),
    taxNo: z.string().trim().regex(/^(\d{10}|\d{11})?$/, t('finance.parties.taxNoInvalid')),
    taxOffice: z.string().trim().max(60),
    bankName: z.string().trim().max(60),
    iban: z.string().refine((v) => !v.trim() || isValidTrIban(v), t('errors.IBAN_INVALID')),
    clearIban: z.boolean(),
    email: z.union([z.literal(''), z.email(t('finance.parties.emailInvalid'))]),
    phone: z.string().trim().max(20),
  })
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: party?.name ?? '',
      kind: party?.kind ?? 'supplier',
      taxNo: party?.taxNo ?? '',
      taxOffice: party?.taxOffice ?? '',
      bankName: party?.bankName ?? '',
      iban: '',
      clearIban: false,
      email: party?.email ?? '',
      phone: party?.phone ?? '',
    },
  })
  const { errors } = form.formState
  const onSubmit = form.handleSubmit(async (input) => {
    try {
      await save.mutateAsync({ id: party?.id, input })
      toast.success(t(party ? 'finance.parties.updated' : 'finance.parties.created'))
      onDone()
    } catch (e) {
      if (isApiError(e) && e.code === 'PARTY_NAME_TAKEN') form.setError('name', { message: t('errors.PARTY_NAME_TAKEN') })
      else toastError(e)
    }
  })
  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<Building2 />}
      title={t(party ? 'finance.parties.editTitle' : 'finance.parties.createTitle')}
      description={t('finance.parties.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <FormSection title={t('finance.parties.sectionGeneral')}>
          <Field id="pt-name" label={t('finance.parties.name')} error={errors.name?.message} full>
            <Input id="pt-name" aria-invalid={!!errors.name} {...form.register('name')} />
          </Field>
          <Field id="pt-kind" label={t('finance.tx.form.kind')}>
            <FormSelect id="pt-kind" control={form.control} name="kind" options={(['supplier', 'customer'] as const).map((k) => ({ value: k, label: t(`finance.parties.kind.${k}`) }))} />
          </Field>
          <Field id="pt-taxno" label={t('finance.parties.taxNo')} error={errors.taxNo?.message}>
            <Input id="pt-taxno" inputMode="numeric" maxLength={11} {...form.register('taxNo')} />
          </Field>
          <Field id="pt-office" label={t('finance.parties.taxOffice')}>
            <Input id="pt-office" {...form.register('taxOffice')} />
          </Field>
          <Field id="pt-phone" label={t('finance.parties.phone')}>
            <Input id="pt-phone" inputMode="tel" {...form.register('phone')} />
          </Field>
          <Field id="pt-email" label={t('finance.parties.email')} error={errors.email?.message} full>
            <Input id="pt-email" type="email" {...form.register('email')} />
          </Field>
        </FormSection>
        <FormSection title={t('finance.parties.sectionBank')}>
          <Field id="pt-bank" label={t('finance.parties.bank')}>
            <Input id="pt-bank" {...form.register('bankName')} />
          </Field>
          <Field id="pt-iban" label={t('finance.accounts.iban')} error={errors.iban?.message} hint={party?.ibanMasked ? t('finance.accounts.form.ibanKeep', { masked: party.ibanMasked }) : undefined}>
            <Input id="pt-iban" className="font-mono uppercase" placeholder="TR00 0000 …" autoComplete="off" aria-invalid={!!errors.iban} {...form.register('iban')} />
          </Field>
          {party?.ibanMasked ? <CheckRow {...form.register('clearIban')}>{t('finance.accounts.form.clearIban')}</CheckRow> : null}
        </FormSection>
      </div>
    </Modal>
  )
}
